/**
 * ClaimView — Upgrade a guest account to a full account by adding email + password.
 * Calls POST /identity/claim with the existing guest JWT.
 */

import type { AweniContext } from '../../../context/AweniContext';
import { createAuthLayout } from '../components/AuthLayout';
import { validateEmail, validatePassword } from '../validators';
import { claimAccount } from '../AuthService';

const TOKEN_KEY = 'learnature_token';

export async function mountClaimView(
  container: HTMLElement,
  ctx: AweniContext,
  opts: { onNavigate: (view: string, params?: Record<string, string>) => void },
): Promise<{ unmount: () => void }> {
  const t = ctx.i18n.t.bind(ctx.i18n);
  const layout = createAuthLayout(ctx);
  layout.setTitle(t('auth_claim_title'));
  layout.setSubtitle(t('auth_claim_subtitle'));

  // If the user is not a guest (or not authed), redirect to the right place.
  const guestToken = localStorage.getItem(TOKEN_KEY);
  if (!guestToken) {
    opts.onNavigate('entry');
    return { unmount: () => {} };
  }

  let loading = false;

  const form = document.createElement('form');
  form.setAttribute('novalidate', '');
  form.autocomplete = 'on';

  // Email
  const emailField = _createField('email', t('auth_email_label'), 'email', 'email');
  form.appendChild(emailField.wrapper);

  // Password
  const pwField = _createField('password', t('auth_password_label'), 'password', 'new-password');
  form.appendChild(pwField.wrapper);

  // Submit
  const submitBtn = document.createElement('button');
  submitBtn.type = 'submit';
  submitBtn.className = 'auth-btn auth-btn-primary';
  submitBtn.innerHTML = `<span>${t('auth_claim_submit')}</span>`;
  form.appendChild(submitBtn);

  // Back
  const backRow = document.createElement('div');
  backRow.className = 'auth-footer-row';
  const backLink = document.createElement('button');
  backLink.type = 'button';
  backLink.className = 'auth-link';
  backLink.textContent = t('auth_guest_back');
  backLink.addEventListener('click', () => ctx.navigate('/home'));
  backRow.appendChild(backLink);
  form.appendChild(backRow);

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (loading) return;

    layout.clearMessages();

    const email = emailField.input.value.trim();
    const password = pwField.input.value;

    const emailResult = validateEmail(email);
    if (!emailResult.valid) {
      layout.showError(t(emailResult.errors[0]));
      emailField.input.focus();
      return;
    }
    const pwResult = validatePassword(password);
    if (!pwResult.valid) {
      layout.showError(t(pwResult.errors[0]));
      pwField.input.focus();
      return;
    }

    loading = true;
    submitBtn.disabled = true;
    submitBtn.innerHTML = `<span class="auth-spinner"></span>`;

    const result = await claimAccount(guestToken, email, password);

    if (result.ok) {
      // Refresh JWT (claim returns a new one with is_guest=0)
      if (result.data.token) {
        localStorage.setItem(TOKEN_KEY, result.data.token);
        document.cookie = `${TOKEN_KEY}=${result.data.token};path=/;max-age=86400;SameSite=Lax`;
      }
      layout.showSuccess(t('auth_claim_success'));
      submitBtn.style.display = 'none';
      window.dispatchEvent(new CustomEvent('aweni:auth-changed'));
      // D10 — honor `?return=/<route>` so users routed here from a D9 guest
      // gate (Upgrade, Family invite, Parental, etc.) land back on the
      // surface that prompted the claim. Same-origin only — `?return=` is
      // path-only (must start with '/' and contain no scheme/host) so an
      // attacker cannot redirect to an external URL via a crafted link.
      let returnPath = '/home';
      try {
        const params = new URLSearchParams(window.location.hash.split('?')[1] || window.location.search);
        const r = params.get('return') || '';
        if (r.startsWith('/') && !r.startsWith('//') && !/[\r\n]/.test(r)) {
          returnPath = r;
        }
      } catch { /* fall through to /home */ }
      setTimeout(() => ctx.navigate(returnPath), 2500);
    } else {
      const err = result.error;
      let msg = err.code === 'EMAIL_EXISTS'
        ? t('auth_claim_error_email_taken')
        : t(`auth_error_${err.code?.toLowerCase() || 'unknown'}`);
      if (msg.startsWith('auth_error_')) msg = err.error;
      layout.showError(msg);
    }

    loading = false;
    submitBtn.disabled = false;
    submitBtn.innerHTML = `<span>${t('auth_claim_submit')}</span>`;
  });

  layout.card.appendChild(form);
  container.innerHTML = '';
  container.appendChild(layout.wrapper);

  return {
    unmount() {
      container.innerHTML = '';
    },
  };
}

function _createField(
  name: string,
  label: string,
  type: string,
  autocomplete: string,
): { wrapper: HTMLElement; input: HTMLInputElement } {
  const wrapper = document.createElement('div');
  wrapper.className = 'auth-field';

  const labelEl = document.createElement('label');
  labelEl.className = 'auth-label';
  labelEl.textContent = label;
  labelEl.setAttribute('for', `auth-claim-${name}`);

  const input = document.createElement('input');
  input.className = 'auth-input';
  input.type = type;
  input.id = `auth-claim-${name}`;
  input.name = name;
  input.autocomplete = autocomplete as AutoFill;
  input.setAttribute('aria-label', label);

  wrapper.append(labelEl, input);
  return { wrapper, input };
}
